/**
 * PKG-013: Runtime Hydration Guard
 * 
 * LAW: 
 * "On any deploy or identity change, frontend runtime state must be discarded 
 * and rebuilt from backend truth."
 * 
 * ENFORCEMENT:
 * This script runs BEFORE React hydration.
 * It compares the current BUILD_ID (from build artifact) against 
 * the 'SYS_BUILD_ANCHOR' stored in localStorage.
 * 
 * BEHAVIOR:
 * Match -> Proceed
 * Mismatch -> Nuke localStorage/sessionStorage -> Set Anchor -> Reload
 */

import { BUILD_ID } from "../../BUILD_ID";
import { UI_INVARIANTS } from "../../config/ui-invariants";

export const enforceRuntimeHydration = () => {
    const STORAGE_KEY = 'SYS_BUILD_ANCHOR';

    try {
        const cachedAnchor = UI_INVARIANTS.STORAGE.getIdentity();

        if (!cachedAnchor) {
            // Case 1: First Boot (Initialization)
            console.log(`[PKG-013] Identity Initialization: ${BUILD_ID}`);
            UI_INVARIANTS.STORAGE.setIdentity(BUILD_ID);
        } else if (cachedAnchor !== BUILD_ID) {
            // Case 2: Mismatch (Drift Detected)
            console.warn(`[PKG-013] Identity Mismatch detected.`);
            console.warn(`Local: ${cachedAnchor} | Remote: ${BUILD_ID}`);
            console.warn(`[PKG-013] Enforcing Nuclear State Reset...`);

            // 1. Nuke State
            UI_INVARIANTS.STORAGE.clearAll();

            // 4. Determine if we should block
            const shouldBlock = UI_INVARIANTS.STATE.shouldBlock(UI_INVARIANTS.PHASES.BOOTSTRAP);

            if (shouldBlock) {
                console.log('[PKG-013] State Reset Complete. Reloading...');
                window.location.reload();
                throw new Error("PKG-013: State Reset Enforced. Reloading...");
            } else {
                // We still set the anchor to prevent infinite reload loops if we ever move to RENDER phase
                UI_INVARIANTS.STORAGE.setIdentity(BUILD_ID);
            }
        } else {
            console.log(`[PKG-013] Identity Verified: ${BUILD_ID}`);
        }
    } catch (e) {
        if (e.message.includes("PKG-013")) {
            throw e; // Propagate the halt
        }
        // Fallback: If storage is inaccessible, we proceed but warn
        console.error("[PKG-013] Storage Check Failed:", e);
    }
};
